//@author: devin

import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    forwardRef,
    Input,
    NgZone,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewEncapsulation,
} from "@angular/core";
import { S25DropdownAbstract } from "../s25.dropdown.abstract";
import { S25Util } from "../../../util/s25-util";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { SearchService } from "../../../services/search/search.service";
import { S25Const } from "../../../util/s25-const";
import { Api } from "../../../api/api";
import { DropdownApi } from "../dropdown.api";
import { UserprefService } from "../../../services/userpref.service";
import { Item } from "../../../pojo/Item";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

export class SearchDropdownApi extends Api {
    static refresh(relativeElem: HTMLElement, items: any[]) {
        return SearchDropdownApi.callApiFn(relativeElem, "s25-ng-search-dropdown", "refresh", items);
    }

    static reloadSearches(relativeElem: HTMLElement, data: any) {
        return SearchDropdownApi.callApiFn(relativeElem, "s25-ng-search-dropdown", "reloadSearches", data);
    }

    static selectSearch(relativeElem: HTMLElement, data: any) {
        return SearchDropdownApi.callApiFn(relativeElem, "s25-ng-search-dropdown", "selectSearch", data);
    }
}

export type ReloadParams = { itemTypeId: Item.Ids; isFullReload?: boolean };
type SelectParams = { property: string; value: unknown; callback?: () => void };
export type ReloadAndSelectParams = ReloadParams & SelectParams;

@TypeManagerDecorator("s25-ng-search-dropdown")
@Component({
    selector: "s25-ng-search-dropdown",
    template: `
        @if (items) {
            <s25-generic-dropdown
                [value]="value()"
                [hasFav]="hasFav"
                [onSelect]="onSelect"
                [items]="items"
                [staticItems]="staticItems"
                [autoOpen]="autoOpen"
                [alwaysOpen]="alwaysOpen"
                [hideSearch]="hideSearch"
                [focusFirst]="false"
                [searchEnabled]="searchEnabled"
                [apiBean]="apiBean"
                [serverSide]="serverSide"
                [placeholder]="placeholder"
                [ariaLabel]="ariaLabel"
                [resetSelectedOnCleanup]="resetSelectedOnCleanup"
                [hasNoneItem]="hasNoneItem"
                [ariaLabel]="ariaLabel"
                (valueChange)="onValueChange($event)"
            />
        }
    `,
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => S25SearchDropdownComponent), multi: true }],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25SearchDropdownComponent<T extends string | number = string | number>
    extends S25DropdownAbstract<T>
    implements OnInit, OnChanges, ControlValueAccessor
{
    // TODO: type merge into itemTypeId and make it required
    @Input() type?: Item.Subject;
    @Input() itemTypeId?: Item.Ids;
    @Input() allowNonQueryId: boolean;
    @Input() additionalSearches: any[];
    @Input() placeholder: string;
    @Input() initialSearch: any; // Argument to selectSearch
    @Input() onlyPublic: boolean = false;

    initSearchPromise: Promise<any>;

    constructor(
        private zone: NgZone,
        private elementRef: ElementRef,
    ) {
        super();
        this.elementRef.nativeElement.angBridge = this;
    }

    getSearches = (isReload?: boolean): Promise<any[]> => {
        const method = isReload ? SearchService.reloadStrictSearches : SearchService.getStrictSearches;
        return method(this.itemTypeId!, this.allowNonQueryId).then((data) => {
            let searches = data?.opt || [];
            if (this.onlyPublic) searches = searches.filter((search: any) => search.grp === "Public Searches");
            return searches.concat(this.additionalSearches);
        });
    };

    reloadSearches = (data?: ReloadParams | ReloadAndSelectParams) => {
        if (data && (data.itemTypeId === this.itemTypeId || data.itemTypeId - 100 === this.itemTypeId)) {
            const isFullReload = S25Util.coalesce(data.isFullReload, true);
            return this.getSearches(isFullReload).then((searches) => {
                this.refresh(searches);
                return this.selectSearch(data); //allow API callers to specify a new chosen search inside this reload request
            });
        }
    };

    selectSearch = (data: ReloadParams | ReloadAndSelectParams) => {
        if (
            data &&
            "property" in data &&
            "value" in data &&
            (data.itemTypeId === this.itemTypeId || data.itemTypeId - 100 === this.itemTypeId)
        ) {
            return this.initSearchPromise?.then(() => {
                //wait for init if search selection request comes in while still going (has no effect if already init)
                return this.zone.run(() => {
                    const newModel = S25Util.propertyGetParentWithChildValue(this.items, data.property, data.value);
                    if (newModel) {
                        this.onValueChange(newModel);
                        try {
                            this.cd.detectChanges();
                        } catch (error: any) {}
                        return this.zone.run(() => {
                            this.onSelect && this.onSelect(this.value());
                            return this.zone.run(() => {
                                data.callback?.();
                                try {
                                    this.cd.detectChanges();
                                } catch (error: any) {}
                                return newModel;
                            });
                        });
                    }
                });
            });
        }
    };

    refresh = (items: any[]) => {
        this.items = items;
        DropdownApi.refresh(this.elementRef.nativeElement, this.items);
        try {
            this.cd.detectChanges();
        } catch (error: any) {}
    };

    ngOnInit() {
        this.itemTypeId ??= S25Const.itemName2Id[this.type!];
        this.allowNonQueryId = S25Util.toBool(S25Util.coalesce(this.allowNonQueryId, true));
        this.additionalSearches = this.additionalSearches || [];
        this.searchEnabled = true;
        const defaultPlaceholder = this.onlyPublic ? "Select a public search" : "Select a search";
        this.placeholder = this.placeholder || defaultPlaceholder;

        this.initSearchPromise = S25Util.all({
            isLoggedIn: UserprefService.getLoggedIn(),
            items: this.getSearches(),
        }).then((resp) => {
            this.hasFav = resp.isLoggedIn;
            this.items ??= resp.items; // Race condition. Only set default items if no other component set items already
            if (this.initialSearch) this.selectSearch(this.initialSearch);
            this.onLoad && this.onLoad();
            try {
                this.cd.detectChanges();
            } catch (error: any) {}
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.itemTypeId && !changes.itemTypeId.isFirstChange()) {
            this.reloadSearches({ itemTypeId: this.itemTypeId! });
        }
    }
}
